/**
 * assets/abibelle-review-list.js
 * Interactions uniquement.
 * Toute la structure HTML est dans snippets/abibelle-review-list.liquid.
 * Tout le style est dans assets/abibelle-review-list.css.
 * Les données viennent de Vercel — aucune décision visuelle ici.
 *
 * Important : aucune décision esthétique ne doit être prise dans ce fichier.
 * Toute modification de l'ordre, de la disposition ou du design des avis doit
 * être réalisée uniquement dans les fichiers du thème Shopify (.liquid et .css),
 * sans modifier ce fichier.
 *
 * Étape 4 : chargement, liste, filtres, pagination.
 * Étape 6 : bouton Utile (endpoint helpful) — reporté.
 * Étape 7 : carrousel photos et viewer.
 */

(function () {
  'use strict';

  /* ── Formatage date (fr-CA, côté client) ────────────────────────────────── */
  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat('fr-CA', {
        year: 'numeric', month: 'long', day: 'numeric',
      }).format(new Date(iso));
    } catch { return ''; }
  }

  /* ── Étoiles — même SVG path que abibelle-reviews.liquid ───────────────── */
  const STAR_PATH = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';

  function buildStarSVG(filled) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.classList.add('abibelle-review-list__star-svg');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', STAR_PATH);
    path.classList.add(filled ? 'abibelle-reviews__star-path--full' : 'abibelle-reviews__star-path--empty');
    svg.appendChild(path);
    return svg;
  }

  function buildStars(rating, container) {
    const count = Math.min(5, Math.max(1, Math.round(rating)));
    container.setAttribute('aria-label', `${count} étoiles sur 5`);
    container.innerHTML = '';
    for (let i = 1; i <= 5; i++) {
      const wrap = document.createElement('span');
      wrap.className = 'abibelle-review-list__star-wrap';
      wrap.appendChild(buildStarSVG(i <= count));
      container.appendChild(wrap);
    }
  }

  /* ── Init idempotente ───────────────────────────────────────────────────── */
  function init() {
    document.querySelectorAll('[data-abibelle-review-list]:not([data-rl-init])').forEach(root => {
      root.dataset.rlInit = '1';
      new ReviewList(root);
    });
  }

  /* ── Tri et filtre ──────────────────────────────────────────────────────── */
  function sortDefault(reviews) {
    return [...reviews].sort((a, b) => {
      const aHasPhoto = (a.photos?.length ?? 0) > 0 ? 1 : 0;
      const bHasPhoto = (b.photos?.length ?? 0) > 0 ? 1 : 0;
      if (bHasPhoto !== aHasPhoto) return bHasPhoto - aHasPhoto;
      if (b.rating !== a.rating) return b.rating - a.rating;
      return dateMs(b) - dateMs(a);
    });
  }

  function sortRecent(reviews) {
    return [...reviews].sort((a, b) => dateMs(b) - dateMs(a));
  }

  function sortOldest(reviews) {
    return [...reviews].sort((a, b) => dateMs(a) - dateMs(b));
  }

  function sortPhotosFilter(reviews) {
    return reviews
      .filter(r => (r.photos?.length ?? 0) > 0)
      .sort((a, b) => {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return dateMs(b) - dateMs(a);
      });
  }

  function sortByRating(reviews, rating) {
    return reviews
      .filter(r => r.rating === rating)
      .sort((a, b) => {
        const aHasPhoto = (a.photos?.length ?? 0) > 0 ? 1 : 0;
        const bHasPhoto = (b.photos?.length ?? 0) > 0 ? 1 : 0;
        if (bHasPhoto !== aHasPhoto) return bHasPhoto - aHasPhoto;
        return dateMs(b) - dateMs(a);
      });
  }

  function dateMs(review) {
    return review.submittedAt ? new Date(review.submittedAt).getTime() : 0;
  }

  function applyFilterAndSort(allReviews, filter) {
    switch (filter) {
      case 'all':    return sortDefault(allReviews);
      case 'recent': return sortRecent(allReviews);
      case 'oldest': return sortOldest(allReviews);
      case 'photos': return sortPhotosFilter(allReviews);
      case '5': return sortByRating(allReviews, 5);
      case '4': return sortByRating(allReviews, 4);
      case '3': return sortByRating(allReviews, 3);
      case '2': return sortByRating(allReviews, 2);
      case '1': return sortByRating(allReviews, 1);
      default:   return sortDefault(allReviews);
    }
  }

  const FILTER_LABELS = {
    all:    'Tous les avis',
    recent: 'Plus récents',
    oldest: 'Plus anciens',
    photos: 'Avec photos',
    '5': '5 ★',
    '4': '4 ★',
    '3': '3 ★',
    '2': '2 ★',
    '1': '1 ★',
  };

  /* ── Classe principale ──────────────────────────────────────────────────── */
  function ReviewList(root) {
    const cfg          = window.AbibelleReviewConfig || {};
    const apiBase      = cfg.apiBase      || '';
    const perPage      = cfg.perPage      ?? 5;
    const scrollOffset = cfg.scrollOffset ?? 80;
    const groupId      = root.dataset.productGroupId || '';

    if (!groupId || !apiBase) return;

    /* ── Slots DOM ──────────────────────────────────────────────────────── */
    const elControls      = root.querySelector('[data-review-controls]');
    const elFilterBtn     = root.querySelector('[data-filter-toggle]');
    const elFilterLabel   = root.querySelector('[data-filter-label]');
    const elFilterMenu    = root.querySelector('[data-filter-menu]');
    const elFilterOpts    = root.querySelectorAll('[data-filter-option]');
    const elItems         = root.querySelector('[data-review-items]');
    const elMoreWrap      = root.querySelector('[data-review-more-wrap]');
    const elMoreBtn       = root.querySelector('[data-review-more-btn]');
    const cardTpl         = root.querySelector('[data-review-card-template]');
    const elCarousel      = root.querySelector('[data-review-carousel]');
    const elCarouselTrack = root.querySelector('[data-carousel-track]');
    const elViewer        = root.querySelector('[data-review-viewer]');
    const elViewerImg     = root.querySelector('[data-viewer-img]');
    const elViewerName    = root.querySelector('[data-viewer-name]');
    const elViewerStars   = root.querySelector('[data-viewer-stars]');
    const elViewerDate    = root.querySelector('[data-viewer-date]');
    const elViewerBody    = root.querySelector('[data-viewer-body]');
    const elViewerClose   = root.querySelector('[data-viewer-close]');
    const elViewerPrev    = root.querySelector('[data-viewer-prev]');
    const elViewerNext    = root.querySelector('[data-viewer-next]');
    const elViewerBackdrop = root.querySelector('[data-viewer-backdrop]');

    const filterBars = document.querySelectorAll('[data-rating-filter]');

    /* ── État ───────────────────────────────────────────────────────────── */
    let allReviews      = [];
    let filteredReviews = [];
    let activeFilter    = 'all';
    let shownCount      = 0;
    let menuOpen        = false;

    /* Viewer */
    let viewerPhotos = [];
    let viewerIndex  = 0;

    /* ── Fetch ──────────────────────────────────────────────────────────── */
    fetch(`${apiBase}/api/reviews/list?product_group_id=${encodeURIComponent(groupId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.ok || !data.reviews?.length) return;

        allReviews = data.reviews;

        root.hidden = false;
        elControls && (elControls.hidden = false);

        updateDisabledOptions();
        bindFilterMenu();
        bindFilterBars();
        elMoreBtn && elMoreBtn.addEventListener('click', showMore);
        initViewer();
        buildCarousel(allReviews);  // ← viewerPhotos construit ici
        applyFilter('all');         // ← cartes générées après, index déjà disponibles
      })
      .catch(err => console.warn('[abibelle-review-list] Erreur fetch:', err));

    /* ── Options désactivées ────────────────────────────────────────────── */
    function updateDisabledOptions() {
      const hasPhotos = allReviews.some(r => (r.photos?.length ?? 0) > 0);
      const ratingCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      allReviews.forEach(r => {
        if (ratingCounts[r.rating] !== undefined) ratingCounts[r.rating]++;
      });

      elFilterOpts.forEach(opt => {
        const val = opt.dataset.filterOption;
        let disabled = false;
        if (val === 'photos') {
          disabled = !hasPhotos;
        } else if (['5','4','3','2','1'].includes(val)) {
          disabled = ratingCounts[Number(val)] === 0;
        }
        opt.classList.toggle('abibelle-review-list__filter-option--disabled', disabled);
        opt.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
    }

    /* ── Carrousel global ───────────────────────────────────────────────── */
    function buildCarousel(reviews) {
      if (!elCarousel || !elCarouselTrack) return;

      /* Trier par submittedAt décroissant — photos les plus récentes en premier */
      const withPhotos = reviews
        .filter(r => (r.photos?.length ?? 0) > 0)
        .sort((a, b) => dateMs(b) - dateMs(a));

      if (!withPhotos.length) {
        elCarousel.hidden = true;
        elCarousel.setAttribute('aria-hidden', 'true');
        return;
      }

      elCarouselTrack.innerHTML = '';

      /* Construire viewerPhotos — toutes les photos, ordre conservé par avis */
      viewerPhotos = [];
      withPhotos.forEach(review => {
        review.photos.forEach(photo => {
          viewerPhotos.push({ url: photo.url, alt: photo.alt || '', review });
        });
      });

      /* ── 3 vignettes photos ─────────────────────────────────────────── */
      viewerPhotos.slice(0, 3).forEach((item, idx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'abibelle-review-photos__thumb';
        btn.setAttribute('aria-label', `Voir la photo ${idx + 1}`);

        const img = document.createElement('img');
        img.src = item.url;
        img.alt = item.alt;
        img.loading = 'lazy';

        btn.appendChild(img);
        btn.addEventListener('click', () => openViewer(idx));
        elCarouselTrack.appendChild(btn);
      });

      /* ── Bouton + si plus de 3 photos ───────────────────────────────── */
      if (viewerPhotos.length > 3) {
        const moreBtn = document.createElement('button');
        moreBtn.type = 'button';
        moreBtn.className = 'abibelle-review-photos__more-btn';
        moreBtn.setAttribute('aria-label', 'Voir toutes les photos');
        moreBtn.addEventListener('click', () => openViewer(0));
        elCarouselTrack.appendChild(moreBtn);
      }

      elCarousel.hidden = false;
      elCarousel.setAttribute('aria-hidden', 'false');
    }

    /* ── Photos dans les cartes ─────────────────────────────────────────── */
    function buildCardPhotos(review, slotPhotos) {
      if (!review.photos?.length) {
        slotPhotos.hidden = true;
        return;
      }

      slotPhotos.hidden = false;
      slotPhotos.innerHTML = '';

      const globalStart = viewerPhotos.findIndex(
        item => item.review.id === review.id
      );

      review.photos.forEach((photo, localIdx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'abibelle-review-list__card-photo-btn';
        btn.setAttribute('aria-label', `Voir la photo ${localIdx + 1}`);

        const img = document.createElement('img');
        img.src = photo.url;
        img.alt = photo.alt || '';
        img.loading = 'lazy';

        btn.appendChild(img);

        const globalIdx = globalStart >= 0 ? globalStart + localIdx : 0;
        btn.addEventListener('click', () => openViewer(globalIdx));
        slotPhotos.appendChild(btn);
      });
    }

    /* ── Viewer ─────────────────────────────────────────────────────────── */
    function initViewer() {
      if (!elViewer || elViewer.dataset.viewerInit) return;
      elViewer.dataset.viewerInit = '1';

      elViewerClose    && elViewerClose.addEventListener('click', closeViewer);
      elViewerBackdrop && elViewerBackdrop.addEventListener('click', closeViewer);
      elViewerPrev     && elViewerPrev.addEventListener('click', () => navigateViewer(-1));
      elViewerNext     && elViewerNext.addEventListener('click', () => navigateViewer(1));

      document.addEventListener('keydown', e => {
        if (elViewer.hidden) return;
        if (e.key === 'Escape')     closeViewer();
        if (e.key === 'ArrowLeft')  navigateViewer(-1);
        if (e.key === 'ArrowRight') navigateViewer(1);
      });

      /* ── Swipe mobile ─────────────────────────────────────────────────── */
      let touchStartX = 0;
      let touchStartY = 0;

      elViewer.addEventListener('touchstart', e => {
        if (!e.changedTouches.length) return;
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
      }, { passive: true });

      elViewer.addEventListener('touchend', e => {
        if (!e.changedTouches.length) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        navigateViewer(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    function openViewer(index) {
      if (!elViewer || !viewerPhotos.length) return;
      viewerIndex = Math.max(0, Math.min(index, viewerPhotos.length - 1));
      renderViewer();
      elViewer.hidden = false;
      document.body.style.overflow = 'hidden';
      elViewerClose && elViewerClose.focus();
    }

    function closeViewer() {
      if (!elViewer) return;
      elViewer.hidden = true;
      document.body.style.overflow = '';
    }

    function navigateViewer(dir) {
      if (!viewerPhotos.length) return;
      viewerIndex = (viewerIndex + dir + viewerPhotos.length) % viewerPhotos.length;
      renderViewer();
    }

    function renderViewer() {
      const item   = viewerPhotos[viewerIndex];
      const review = item.review;

      if (elViewerImg) {
        elViewerImg.src = item.url;
        elViewerImg.alt = item.alt || review.reviewerName;
      }
      if (elViewerName)  elViewerName.textContent = review.reviewerName;
      if (elViewerStars) buildStars(review.rating, elViewerStars);
      if (elViewerDate) {
        elViewerDate.textContent = formatDate(review.submittedAt);
        elViewerDate.setAttribute('datetime', review.submittedAt || '');
      }
      if (elViewerBody) elViewerBody.textContent = review.body;

      if (elViewerPrev) elViewerPrev.hidden = viewerPhotos.length <= 1;
      if (elViewerNext) elViewerNext.hidden = viewerPhotos.length <= 1;
    }

    /* ── Menu déroulant ─────────────────────────────────────────────────── */
    function bindFilterMenu() {
      if (!elFilterBtn || !elFilterMenu) return;

      elFilterBtn.addEventListener('click', toggleMenu);

      elFilterOpts.forEach(opt => {
        opt.addEventListener('click', () => {
          if (opt.getAttribute('aria-disabled') === 'true') return;
          const filter = opt.dataset.filterOption;
          selectFilter(filter);
          closeMenu();
        });
      });

      document.addEventListener('click', e => {
        if (menuOpen && !elFilterBtn.contains(e.target) && !elFilterMenu.contains(e.target)) {
          closeMenu();
        }
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && menuOpen) closeMenu();
      });
    }

    function toggleMenu() { menuOpen ? closeMenu() : openMenu(); }

    function openMenu() {
      menuOpen = true;
      elFilterMenu.hidden = false;
      elFilterBtn.setAttribute('aria-expanded', 'true');
    }

    function closeMenu() {
      menuOpen = false;
      elFilterMenu.hidden = true;
      elFilterBtn.setAttribute('aria-expanded', 'false');
    }

    function selectFilter(filter) {
      activeFilter = filter;

      if (elFilterLabel) {
        elFilterLabel.textContent = FILTER_LABELS[filter] || 'Tous les avis';
      }

      elFilterOpts.forEach(opt => {
        const isActive = opt.dataset.filterOption === filter;
        opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
        opt.classList.toggle('abibelle-review-list__filter-option--active', isActive);
      });

      filterBars.forEach(bar => {
        bar.setAttribute('aria-pressed', bar.dataset.ratingFilter === filter ? 'true' : 'false');
      });

      applyFilter(filter);
    }

    /* ── Barres de distribution ─────────────────────────────────────────── */
    function bindFilterBars() {
      filterBars.forEach(bar => {
        const rating = bar.dataset.ratingFilter;
        function activate(e) {
          if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          selectFilter(rating);
          scrollToList();
        }
        bar.addEventListener('click', activate);
        bar.addEventListener('keydown', activate);
      });
    }

    /* ── Filtre + tri ───────────────────────────────────────────────────── */
    function applyFilter(filter) {
      filteredReviews = applyFilterAndSort(allReviews, filter);
      shownCount = 0;
      if (elItems) elItems.innerHTML = '';
      showMore();
    }

    function showMore() {
      const slice = filteredReviews.slice(shownCount, shownCount + perPage);
      slice.forEach(review => elItems && elItems.appendChild(buildCard(review)));
      shownCount += slice.length;
      if (elMoreWrap) elMoreWrap.hidden = shownCount >= filteredReviews.length;
    }

    function scrollToList() {
      if (!root) return;
      const top = root.getBoundingClientRect().top + window.scrollY - scrollOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    /* ── Construction d'une carte ───────────────────────────────────────── */
    function buildCard(review) {
      if (!cardTpl) return document.createDocumentFragment();
      const clone   = cardTpl.content.cloneNode(true);
      const article = clone.querySelector('[data-review-id]');
      if (article) article.dataset.reviewId = review.id;

      const slotName = clone.querySelector('[data-slot-name]');
      if (slotName) slotName.textContent = review.reviewerName;

      const slotStars = clone.querySelector('[data-slot-stars]');
      if (slotStars) buildStars(review.rating, slotStars);

      const slotDate = clone.querySelector('[data-slot-date]');
      if (slotDate) {
        slotDate.textContent = formatDate(review.submittedAt);
        slotDate.setAttribute('datetime', review.submittedAt || '');
      }

      const slotBody = clone.querySelector('[data-slot-body]');
      if (slotBody) slotBody.textContent = review.body;

      /* Bouton Utile — masqué, activé à l'étape 6 */
      const slotHelpful = clone.querySelector('[data-slot-helpful]');
      if (slotHelpful) {
        slotHelpful.dataset.helpfulId = review.id;
        slotHelpful.hidden = true;
      }

      /* Photos — étape 7 */
      const slotPhotos = clone.querySelector('[data-slot-photos]');
      if (slotPhotos) buildCardPhotos(review, slotPhotos);

      return clone;
    }
  }

  /* ── Lancement ──────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

})();
